import { Box, Button, IconButton } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread } from '@/assets/images';
import { WantedPersonFlag } from '@/components';
import { DATE_FORMAT } from '@/config';
import { RaceList } from '@/constants/form_inputs';
import { MasterNameInvolvement, PhoneNumberType } from '@/models';
import {
  IMasterNameRelationBehavior,
  MasterNameType,
} from '@/models/master.names';
import { formatPersonName } from '@/utils';
import { formatPhoneNumber } from '@/utils/pattern';

import InvolvementInlineSelect from './components/InvolvementInlineSelect';
import { isVehicleLinkedRegisteredOwner } from './registeredOwnerVehicle';

const EMPTY_INVOLVEMENT: string[] = [];

const getDisplayPhoneNumber = (
  phoneNumbers?: {
    phoneNumber?: string;
    type?: string;
  }[],
) => {
  if (!phoneNumbers?.length) {
    return null;
  }

  const callerPhone = phoneNumbers.find(
    (item) => item.type === PhoneNumberType.CALLER && item.phoneNumber,
  )?.phoneNumber;
  if (callerPhone) {
    return callerPhone;
  }

  return phoneNumbers.find((item) => item.phoneNumber)?.phoneNumber || null;
};

const name = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    const master = params.row.masterName;

    if (typeof master === 'string') {
      return `ID: ${master.substring(0, 8)}...`;
    }

    if (master && typeof master === 'object') {
      const isWanted = master.isWanted;
      const fullName = master.name
        ? `${master.name}`
        : formatPersonName(master);

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
          }}
        >
          <span>{fullName || '-'}</span>
          {isWanted && <WantedPersonFlag size="small" />}
        </Box>
      );
    }

    return '-';
  },
  valueGetter: (_, row) => {
    const master = row.masterName;

    if (typeof master === 'string') return master;
    return master?.name ?? '-';
  },
});

const dateOfBirth = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    if (typeof params.row.masterName === 'string') {
      return '-';
    }
    const birthday = params.row.masterName?.birthday;
    return birthday ? moment(birthday).format(DATE_FORMAT) : '-';
  },
  valueGetter: (_, row) => {
    if (typeof row.masterName === 'string') {
      return null;
    }
    return row.masterName?.birthday;
  },
});

const type = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    if (params.row.masterName?.type == 'PERSON') {
      return 'Person';
    }

    return 'Organization';
  },
  valueGetter: (_, row) => {
    if (typeof row.masterName === 'string') {
      return null;
    }
    return row.masterName?.type;
  },
});

const isVerified = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    if (typeof params.row.masterName === 'string') {
      return '-';
    }
    return params.row.masterName?.isVerified ? 'Verified' : 'Unverified';
  },
  valueGetter: (_, row) => {
    if (typeof row.masterName === 'string') {
      return null;
    }
    return row.masterName?.isVerified;
  },
});

const phone = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    if (typeof params.row.masterName === 'string') {
      return '-';
    }
    const phoneNumber = getDisplayPhoneNumber(
      params.row.masterName?.phoneNumbers,
    );
    return phoneNumber ? formatPhoneNumber(phoneNumber) : '-';
  },
  valueGetter: (_, row) => {
    if (typeof row.masterName === 'string') {
      return null;
    }
    return getDisplayPhoneNumber(row.masterName?.phoneNumbers);
  },
});

const involvement = (config: {
  isReadOnly?: boolean;
  onInvolvementUpdate?: (
    nameId: string,
    involvements: MasterNameInvolvement[],
    behavior: IMasterNameRelationBehavior,
  ) => Promise<void>;
}): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    const rawInvolvement = params?.row?.relationship?.involvement;
    const currentInvolvement =
      Array.isArray(rawInvolvement) && rawInvolvement.length > 0
        ? rawInvolvement
        : EMPTY_INVOLVEMENT;
    const currentBehavior = params?.row?.relationship?.behavior;
    const masterName = params.row.masterName;
    const nameId =
      typeof masterName === 'string' ? masterName : masterName?._id;
    const masterNameType =
      typeof masterName === 'string'
        ? MasterNameType.PERSON
        : masterName?.type || MasterNameType.PERSON;

    const isReadOnly = !config.onInvolvementUpdate || config.isReadOnly;

    if (!currentInvolvement.length && isReadOnly) return '-';

    return (
      <InvolvementInlineSelect
        nameId={nameId}
        currentInvolvement={currentInvolvement}
        currentBehavior={currentBehavior}
        masterNameType={masterNameType}
        isReadOnly={isReadOnly}
        disableOwnerRemoval={isVehicleLinkedRegisteredOwner(params.row)}
        onUpdate={config.onInvolvementUpdate || (async () => {})}
      />
    );
  },
  valueGetter: (_, row) => row.relationship?.involvement,
});

const race = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    if (typeof params.row.masterName === 'string') {
      return '-';
    }
    const raceValue = params.row.masterName?.race;
    return RaceList[raceValue as keyof typeof RaceList] || '-';
  },
  valueGetter: (_, row) => {
    if (typeof row.masterName === 'string') {
      return null;
    }
    return row.masterName?.race;
  },
});

const actions = (handlers: {
  onSetInvolvement?: (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => void;
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void;
}): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    const id =
      typeof params.row.masterName === 'string'
        ? params.row.masterName
        : params.row.masterName?._id;

    return (
      <Box
        className="!w-full"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {handlers.onSetInvolvement && (
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={(e) => handlers.onSetInvolvement?.(e, id)}
            sx={{
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto',
              borderRadius: '6px',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Set Involvement
          </Button>
        )}
        {/* <Tooltip title="view notes" arrow>
          <IconButton onClick={(e) => handlers.onGotoNotes?.(e, params.row)}>
            <IcFile />
          </IconButton>
        </Tooltip> */}
        {handlers.openMenu && (
          <IconButton
            aria-label="actions"
            onClick={(e) => handlers.openMenu?.(e, params.row)}
          >
            <IcDotSpread />
          </IconButton>
        )}
      </Box>
    );
  },
});

export const masterNamesCols = (handlers: {
  navigate?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onSetInvolvement?: (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => void;
  onGotoNotes?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void;
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void;
  isKase?: boolean;
  isReadOnly?: boolean;
  onInvolvementUpdate?: (
    nameId: string,
    involvements: MasterNameInvolvement[],
    behavior: IMasterNameRelationBehavior,
  ) => Promise<void>;
}) => ({
  name,
  dateOfBirth,
  type,
  isVerified,
  phone,
  involvement: () =>
    involvement({
      isReadOnly: handlers.isReadOnly,
      onInvolvementUpdate: handlers.onInvolvementUpdate,
    }),
  race,
  actions: () => actions(handlers),
});
